import { useCallback } from 'react';
import { GRID_LAYOUT_COLS, GRID_LAYOUT_SMALL_BREAKPOINT } from '../../constants';
import { PanelGroupGridDefinition, PanelGroupItemLayout } from '../../context';
import { LayoutPreset } from './CleanLayoutOptions';

const GRID_COLUMN_COUNT = GRID_LAYOUT_COLS[GRID_LAYOUT_SMALL_BREAKPOINT];

const PRESET_COLUMN_COUNT: Record<LayoutPreset, number> = {
  'keep-existing': -1,
  '2-col': 2,
  '3-col': 3,
  '4-col': 4,
  featured: 3,
  decreasing: -1, // not used directly, handled separately
};

/** Row-by-row column counts for the decreasing-priority preset: 2, 3, then 4+ for all remaining rows. */
const DECREASING_ROW_COLUMNS = [2, 3, 4] as const;

function buildLayoutForPreset(
  panelGroupDefinition: PanelGroupGridDefinition,
  preset: LayoutPreset,
  panelHeight: number
): PanelGroupItemLayout[] | null {
  if (panelGroupDefinition.itemLayouts.length === 0) return null;

  // Keep existing layout but only update panel heights.
  if (preset === 'keep-existing') {
    return panelGroupDefinition.itemLayouts.map((panel) => ({
      ...panel,
      h: panelHeight,
    }));
  }

  const columnCount = PRESET_COLUMN_COUNT[preset];
  const colWidth = GRID_COLUMN_COUNT / columnCount;

  // Decreasing: first row gets 2 columns, second gets 3, remaining rows get 4.
  if (preset === 'decreasing') {
    const result: PanelGroupItemLayout[] = [];
    let currentRowColumnsIdx = 0;
    let currentRowIdx = 0;

    const defaultCount = DECREASING_ROW_COLUMNS[DECREASING_ROW_COLUMNS.length - 1]!;

    panelGroupDefinition.itemLayouts.forEach((panel) => {
      const columnsInRow = DECREASING_ROW_COLUMNS[currentRowColumnsIdx] ?? defaultCount;
      const columnWidth = GRID_COLUMN_COUNT / columnsInRow;

      result.push({
        ...panel,
        w: columnWidth,
        h: panelHeight,
        x: currentRowIdx * columnWidth,
        y: currentRowColumnsIdx * panelHeight,
      });

      currentRowIdx = currentRowIdx + 1;

      if (currentRowIdx === columnsInRow) {
        currentRowIdx = 0;
        currentRowColumnsIdx = currentRowColumnsIdx + 1;
      }
    });

    return result;
  }

  // Featured: first panel is double-width and double-height, rest fill a standard grid.
  if (preset === 'featured') {
    return panelGroupDefinition.itemLayouts.map((panel, idx) => {
      const featuredRowHeight = panelHeight * 2;

      if (idx === 0) {
        return {
          ...panel,
          w: colWidth * 2,
          h: featuredRowHeight,
          x: 0,
          y: 0,
        };
      }

      if (idx < 3) {
        return {
          ...panel,
          x: colWidth * 2,
          y: (idx - 1) * panelHeight,
          w: colWidth,
          h: panelHeight,
        };
      }

      return {
        ...panel,
        x: (idx % columnCount) * colWidth,
        // Offset by one row to account for featured row being an extra row tall:
        y: panelHeight + Math.floor(idx / columnCount) * panelHeight,
        w: colWidth,
        h: panelHeight,
      };
    });
  }

  return panelGroupDefinition.itemLayouts.map((panel, idx) => ({
    ...panel,
    x: (idx % columnCount) * colWidth,
    y: Math.floor(idx / columnCount) * panelHeight,
    w: colWidth,
    h: panelHeight,
  }));
}

/**
 * Hook that returns a function to build a layout for a given preset and panel height.
 * Returns `null` when the panel group definition is not available.
 */
export function useBuildLayoutPreset(panelGroupDefinition: PanelGroupGridDefinition | undefined) {
  return useCallback(
    (preset: LayoutPreset, panelHeight: number) => {
      if (!panelGroupDefinition) return null;
      return buildLayoutForPreset(panelGroupDefinition, preset, panelHeight);
    },
    [panelGroupDefinition]
  );
}
